/**
 * Pinia clients store -- distributor Clients tab.
 *
 * Card / pebble: feature work for the Clients tab under the
 * distributor app's Work section (issue #8 -- "Clients tab under Work
 * section"). A Client is an end-customer business the distributor
 * sells to; a Client owns zero or more Stores via the one-to-many
 * ``stores.client_id`` FK. The Clients tab lets Owners add / edit /
 * delete Clients and manage their Store associations without leaving
 * the distributor app.
 *
 * Endpoints:
 *   - GET    /api/clients/                       -- SAFRS list
 *   - POST   /api/clients/                       -- SAFRS create
 *   - PATCH  /api/clients/<id>                   -- SAFRS update
 *   - DELETE /api/clients/<id>                   -- SAFRS delete
 *   - POST   /api/clients/<id>/stores            -- custom: attach store
 *   - DELETE /api/clients/<id>/stores/<store_id> -- custom: detach store
 *
 * The store attaches + detaches Stores via the custom endpoints (the
 * frontend deliberately does NOT PATCH stores.client_id directly --
 * that would route through the Store editor SAFRS surface, which is
 * cap-gated and shaped for full store-edit flows, not lightweight
 * association management).
 *
 * Tenant + impersonation hygiene
 * ------------------------------
 *
 * Same epoch-guard pattern as ``stores/artworks.js``: ``clear()`` bumps
 * an epoch counter; in-flight fetches/mutations check the epoch before
 * mutating store state so a late-arriving response from a previous
 * tenant cannot repopulate the list after an impersonation stop.
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '@/services/api'

export const useClientsStore = defineStore('clients', () => {
    // ---- state ----
    const clients = ref([])
    const loading = ref(false)
    const error = ref(null)
    // Epoch guard -- bumped on clear() so a late-arriving fetch from a
    // previous tenant cannot repopulate the store after impersonation
    // stops. Mirrors the artworks store pattern.
    let epoch = 0

    // ---- getters ----

    function getClientById(id) {
        if (id == null) return null
        const target = String(id)
        return clients.value.find((c) => String(c.id) === target) ?? null
    }

    // ---- actions ----

    /**
     * Hydrate ``clients`` for the effective tenant.
     *
     * Endpoint: GET /api/clients/ (SAFRS list, tenant-filtered via
     * ``RoleMixin._campaign_perms``). Epoch-guarded so a late response
     * from a previous tenant cannot land.
     */
    async function fetch() {
        const myEpoch = epoch
        loading.value = true
        error.value = null
        try {
            const res = await api.get('/clients/')
            if (myEpoch !== epoch) return
            clients.value = normalizeListResponse(res.data)
        } catch (e) {
            if (myEpoch !== epoch) return
            error.value = e.message || 'clients fetch failed'
            clients.value = []
        } finally {
            if (myEpoch === epoch) loading.value = false
        }
    }

    /**
     * Create a Client.
     *
     * ``payload`` is the plain shape ``{name, email, phone, notes,
     * status}``. We wrap it in the JSON:API envelope SAFRS expects and
     * POST to /api/clients/. The created Client is prepended to the
     * local list so the table re-renders immediately.
     *
     * Optional ``initialStoreIds`` is an array of store ids to attach
     * after the Client is created -- a convenience for the create form
     * which lets the Owner pre-attach existing stores. Each attach is
     * a separate POST; if any fails the Client is still created and
     * present in the list (we surface the attach error to the caller).
     */
    async function createClient(payload, initialStoreIds = []) {
        const myEpoch = epoch
        const body = {
            data: {
                type: 'Client',
                attributes: { ...payload },
            },
        }
        const res = await api.post('/clients/', body)
        const created = normalizeListResponse(res.data)[0] || null
        if (myEpoch === epoch && created) {
            clients.value = [created, ...clients.value]
        }
        if (created && Array.isArray(initialStoreIds) && initialStoreIds.length) {
            for (const sid of initialStoreIds) {
                // Sequential rather than parallel: the surface count is
                // tiny (<10 stores in practice) and serial keeps error
                // recovery simple -- the first failure surfaces and the
                // remainder are skipped.
                // eslint-disable-next-line no-await-in-loop
                await addStoreAssociation(created.id, sid)
            }
        }
        return created
    }

    /**
     * PATCH an existing Client.
     *
     * SAFRS PATCH consumes the JSON:API envelope; we wrap ``patch`` in
     * ``{data: {type, id, attributes}}``. The local row is updated in
     * place on success.
     */
    async function updateClient(id, patch) {
        const myEpoch = epoch
        const body = {
            data: {
                type: 'Client',
                id: String(id),
                attributes: { ...patch },
            },
        }
        const res = await api.patch(`/clients/${id}`, body)
        const updated = normalizeListResponse(res.data)[0] || null
        if (myEpoch !== epoch) return updated
        if (updated) {
            const idx = clients.value.findIndex(
                (c) => String(c.id) === String(id),
            )
            if (idx >= 0) {
                clients.value.splice(idx, 1, updated)
            }
        }
        return updated
    }

    /**
     * Soft-delete a Client by setting ``status`` to ``'inactive'``.
     *
     * The Clients tab replaced hard-delete with soft-delete: a
     * "deactivate" leaves the row in place (still listed + filterable,
     * de-emphasized) rather than removing it, so its stores and history
     * survive and it can be reactivated. Implemented as a thin wrapper
     * over ``updateClient`` -- the status PATCH is the whole operation,
     * and ``updateClient`` already updates the local row in place so the
     * table re-renders the row with its inactive styling.
     */
    async function deactivateClient(id) {
        return updateClient(id, { status: 'inactive' })
    }

    /**
     * DELETE a Client. Removes the row from the local list on 2xx.
     *
     * Retained for completeness / hard-delete callers; the Clients tab
     * list surface now uses ``deactivateClient`` (soft-delete) for the
     * user-facing "delete" affordance.
     */
    async function deleteClient(id) {
        const myEpoch = epoch
        await api.delete(`/clients/${id}`)
        if (myEpoch !== epoch) return
        clients.value = clients.value.filter(
            (c) => String(c.id) !== String(id),
        )
    }

    /**
     * Attach a Store to a Client (custom endpoint).
     *
     * Idempotent at the API layer; this action does NOT update the
     * local row's ``stores`` collection because SAFRS doesn't include
     * Store rows in the default Client list response. Callers that
     * need an up-to-date store count should re-fetch the affected
     * Client (or its stores via a dedicated query).
     */
    async function addStoreAssociation(clientId, storeId) {
        const res = await api.post(`/clients/${clientId}/stores`, {
            store_id: storeId,
        })
        return res.data
    }

    /**
     * Detach a Store from a Client (custom endpoint).
     */
    async function removeStoreAssociation(clientId, storeId) {
        await api.delete(`/clients/${clientId}/stores/${storeId}`)
    }

    /**
     * Reset state + bump epoch so in-flight requests are dropped.
     */
    function clear() {
        epoch += 1
        clients.value = []
        error.value = null
        loading.value = false
    }

    return {
        // state
        clients,
        loading,
        error,
        // getters
        getClientById,
        // actions
        fetch,
        createClient,
        updateClient,
        deactivateClient,
        deleteClient,
        addStoreAssociation,
        removeStoreAssociation,
        clear,
    }
})

/**
 * Coerce a SAFRS or plain list response into a flat array of client
 * dicts. Mirrors the artworks store normalizer. Handles:
 *   - JSON:API: ``{data: [{type, id, attributes}, ...]}``
 *   - Bare list: ``[{...}, {...}]``
 *   - Custom envelope: ``{clients: [...]}``
 *   - JSON:API single-resource: ``{data: {type, id, attributes}}``
 */
export function normalizeListResponse(body) {
    if (Array.isArray(body)) return body
    if (Array.isArray(body?.clients)) return body.clients
    if (Array.isArray(body?.data)) {
        return body.data.map((entry) => ({
            id: entry.id,
            ...(entry.attributes ?? {}),
        }))
    }
    if (body?.data && !Array.isArray(body.data) && body.data.id != null) {
        return [{ id: body.data.id, ...(body.data.attributes ?? {}) }]
    }
    return []
}
